/**
 * OAuth2 / SSO 接入 API（gitman 模式：多 provider + proxy/authorization_code 双流程）
 * 端点:
 *   GET  /api/v1/auth/oauth/providers        (公开) 已启用的 provider 列表
 *   POST /api/v1/auth/oauth/:name/login      (公开) 代理登录(proxy)：中继到 IdP 后签发 mailman JWT
 *   GET  /api/v1/auth/oauth/:name/login      (公开) 标准授权码流程:302 跳转到 IdP 授权页
 *   POST /api/v1/auth/oauth/exchange         (公开) 用一次性 code 换回 token
 */
import apiClient, { unwrap } from './client'
import type { LoginResponse } from './types'

export interface OAuthProviderPublic {
  name: string
  display_name: string
  /** 流程类型:'proxy'(代理登录)或 'authorization_code'(标准授权码 + PKCE) */
  flow: string
}

/** 管理端可见的完整 provider 配置（含 client_secret 等敏感字段） */
export interface OAuthProviderConfig {
  name: string
  display_name: string
  enabled: boolean
  flow: string
  base_url: string
  client_id: string
  client_secret: string
  scopes: string
  admin_role: string
  redirect_uri: string | null
  auto_create: boolean
  authorization_endpoint: string
  token_endpoint: string
  userinfo_endpoint: string
}

/** 系统设置页的 OAuth2 配置（管理端） */
export interface OAuthConfig {
  local_login_enabled: boolean
  oauth_enabled: boolean
  providers: OAuthProviderConfig[]
}

/** 登录页公开配置 */
export interface LoginConfig {
  local_login_enabled: boolean
  oauth_enabled: boolean
  providers: OAuthProviderPublic[]
}

export const oauthApi = {
  /** GET /api/v1/auth/oauth/providers —— 已启用的 provider 列表(登录页按钮用)。
   *  注意：该端点返回裸数组（gitman 风格），非 {success,data} 包装，不能走 unwrap */
  async listProviders(): Promise<OAuthProviderPublic[]> {
    const { data } = await apiClient.get<OAuthProviderPublic[]>('/auth/oauth/providers')
    return data
  },

  /** GET /api/v1/auth/login-config —— 登录页公开配置（本地登录开关 + 已启用 provider）。返回裸对象 */
  async getLoginConfig(): Promise<LoginConfig> {
    const { data } = await apiClient.get<LoginConfig>('/auth/login-config')
    return data
  },

  /** GET /api/v1/oauth/config —— 读取 OAuth2 配置（管理端，含敏感字段） */
  getConfig() {
    return unwrap<OAuthConfig>(apiClient.get('/oauth/config'))
  },

  /** PUT /api/v1/oauth/config —— 全量更新 OAuth2 配置 */
  updateConfig(input: OAuthConfig) {
    return unwrap<OAuthConfig>(apiClient.put('/oauth/config', input))
  },

  /**
   * POST /api/v1/auth/oauth/:name/login —— 代理登录。
   * 后端中继到 IdP 的 authorize/confirm/token/userinfo，再用返回的 profile
   * 签发 mailman 自己的 JWT。返回与本地登录完全相同的结构。
   */
  async login(provider: string, username: string, password: string): Promise<LoginResponse> {
    return unwrap<LoginResponse>(
      apiClient.post(`/auth/oauth/${encodeURIComponent(provider)}/login`, {
        username,
        password,
      }),
    )
  },

  /**
   * 标准授权码流程的「发起」(authorization_code)。
   * 浏览器顶层跳转到 mailman 的 `GET /api/v1/auth/oauth/{provider}/login`,
   * 该端点会写 httpOnly PKCE cookie 后 302 跳转到 IdP 授权页,用户在 IdP 完成登录。
   * 注意:必须是顶层导航(window.location),不能走 fetch/axios —— 否则拿不到 Set-Cookie,
   * 且 IdP 的 302 也无法被 SPA 跟随回到 mailman 的 callback。
   */
  redirectToProviderLogin(provider: string): void {
    window.location.assign(`/api/v1/auth/oauth/${encodeURIComponent(provider)}/login`);
  },

  /**
   * POST /api/v1/auth/oauth/exchange —— 标准授权码流程的「兑换」。
   * 前端回调页(`/oauth/callback?code=...`)读取一次性 code,POST 换回真正的
   * token(与登录完全相同的结构),存入本地。code 单次有效,用完即焚。
   */
  async exchange(code: string): Promise<LoginResponse> {
    return unwrap<LoginResponse>(apiClient.post('/auth/oauth/exchange', { code }))
  },
}
